import { useState, useEffect } from 'react';
import { Eye, ChevronUp, ChevronDown, User, Shield, Users, UserCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface DevModePreviewProps {
  onRoleChange: (role: 'super_admin' | 'team_leader' | 'normal_user' | null) => void;
  currentRole: 'super_admin' | 'team_leader' | 'normal_user' | null;
  onUserChange: (userId: string | null) => void;
  currentUserId: string | null;
}

interface UserOption {
  id: string;
  name: string;
  email: string;
  role: string;
  business_name?: string;
}

export default function DevModePreview({ onRoleChange, currentRole, onUserChange, currentUserId }: DevModePreviewProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [loading, setLoading] = useState(false);

  const isDevelopmentMode = import.meta.env.VITE_DEVELOPMENT_MODE === 'true';

  useEffect(() => {
    if (isDevelopmentMode && (currentRole === 'team_leader' || currentRole === 'normal_user')) {
      loadUsers();
    }
  }, [isDevelopmentMode, currentRole]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const { data: usersData, error: usersError } = await supabase
        .from('users')
        .select('id, first_name, last_name, telegram_username, telegram_id, role')
        .order('first_name');

      if (usersError) {
        console.error('Error loading users:', usersError);
        setUsers([]);
        return;
      }

      const { data: userBusinessData, error: ubError } = await supabase
        .from('user_businesses')
        .select('user_id, businesses(name)');

      if (ubError) {
        console.error('Error loading user businesses:', ubError);
      }

      const businessMap = new Map();
      if (userBusinessData) {
        userBusinessData.forEach((ub: any) => {
          businessMap.set(ub.user_id, ub.businesses?.name || 'No Business');
        });
      }

      const formattedUsers: UserOption[] = (usersData || []).map((user: any) => {
        const displayName = user.first_name
          ? `${user.first_name} ${user.last_name || ''}`.trim()
          : user.telegram_username || 'Unnamed User';

        return {
          id: user.id,
          name: displayName,
          email: user.telegram_username ? `@${user.telegram_username}` : '',
          role: user.role || 'normal_user',
          business_name: businessMap.get(user.telegram_id) || 'No Business',
        };
      });

      console.log('Loaded users:', formattedUsers);
      setUsers(formattedUsers);
    } catch (error) {
      console.error('Error loading users:', error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  if (!isDevelopmentMode) return null;

  const roles = [
    {
      id: null,
      name: 'Default View',
      icon: Eye,
      color: 'gray',
      description: 'Normal user interface'
    },
    {
      id: 'super_admin',
      name: 'Super Admin',
      icon: Shield,
      color: 'purple',
      description: 'Full system access'
    },
    {
      id: 'team_leader',
      name: 'Team Leader',
      icon: Users,
      color: 'blue',
      description: 'Team & business management'
    },
    {
      id: 'normal_user',
      name: 'Normal User',
      icon: User,
      color: 'green',
      description: 'Receipt management only'
    },
  ] as const;

  const currentRoleData = roles.find(r => r.id === currentRole) || roles[0];

  return (
    <div className="fixed bottom-4 right-4 z-50">
      <div className="bg-gradient-to-br from-orange-500 to-red-600 rounded-lg shadow-2xl border-2 border-orange-300 overflow-hidden">
        {/* Header */}
        <div className="bg-black/20 px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
            <span className="text-white text-xs font-bold uppercase tracking-wider">
              Dev Mode Preview
            </span>
          </div>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-white hover:bg-white/20 rounded p-1 transition-colors"
          >
            {isExpanded ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronUp className="w-4 h-4" />
            )}
          </button>
        </div>

        {/* Content */}
        {isExpanded && (
          <div className="p-4 bg-white/10 backdrop-blur-sm">
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-white text-sm mb-2">
                <Eye className="w-4 h-4" />
                <span className="font-semibold">Viewing as:</span>
              </div>

              {roles.map((role) => {
                const Icon = role.icon;
                const isActive = currentRole === role.id;

                return (
                  <button
                    key={role.id || 'default'}
                    onClick={() => onRoleChange(role.id as any)}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-all ${
                      isActive
                        ? 'bg-white text-gray-900 shadow-lg scale-105'
                        : 'bg-white/20 text-white hover:bg-white/30'
                    }`}
                  >
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        isActive
                          ? role.color === 'purple'
                            ? 'bg-purple-500'
                            : role.color === 'blue'
                            ? 'bg-blue-500'
                            : role.color === 'green'
                            ? 'bg-green-500'
                            : 'bg-gray-500'
                          : 'bg-white/30'
                      }`}
                    >
                      <Icon className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex-1 text-left">
                      <p className={`font-semibold text-sm ${isActive ? 'text-gray-900' : 'text-white'}`}>
                        {role.name}
                      </p>
                      <p className={`text-xs ${isActive ? 'text-gray-600' : 'text-white/70'}`}>
                        {isActive ? 'Currently active' : role.description}
                      </p>
                    </div>
                    {isActive && (
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    )}
                  </button>
                );
              })}
            </div>

            {(currentRole === 'team_leader' || currentRole === 'normal_user') && (
              <div className="mt-4 pt-4 border-t border-white/20">
                <div className="flex items-center gap-2 text-white text-sm mb-3">
                  <UserCircle className="w-4 h-4" />
                  <span className="font-semibold">View as User:</span>
                </div>

                {loading ? (
                  <div className="text-white/70 text-xs text-center py-2">Loading users...</div>
                ) : users.length === 0 ? (
                  <div className="bg-white/20 rounded-lg p-3 text-center">
                    <p className="text-white text-xs">No users found in database</p>
                    <p className="text-white/70 text-xs mt-1">Create users first to test</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    <button
                      onClick={() => onUserChange(null)}
                      className={`w-full text-left px-3 py-2 rounded-lg transition-all ${
                        currentUserId === null
                          ? 'bg-white text-gray-900 shadow-lg'
                          : 'bg-white/20 text-white hover:bg-white/30'
                      }`}
                    >
                      <p className={`font-semibold text-xs ${currentUserId === null ? 'text-gray-900' : 'text-white'}`}>
                        No User Selected
                      </p>
                      <p className={`text-xs ${currentUserId === null ? 'text-gray-600' : 'text-white/70'}`}>
                        Default view
                      </p>
                    </button>

                    {users.map((user) => (
                      <button
                        key={user.id}
                        onClick={() => onUserChange(user.id)}
                        className={`w-full text-left px-3 py-2 rounded-lg transition-all ${
                          currentUserId === user.id
                            ? 'bg-white text-gray-900 shadow-lg'
                            : 'bg-white/20 text-white hover:bg-white/30'
                        }`}
                      >
                        <p className={`font-semibold text-xs ${currentUserId === user.id ? 'text-gray-900' : 'text-white'}`}>
                          {user.name}
                        </p>
                        <p className={`text-xs ${currentUserId === user.id ? 'text-gray-600' : 'text-white/70'}`}>
                          {user.business_name} • {user.role}
                        </p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="mt-3 pt-3 border-t border-white/20">
              <p className="text-xs text-white/80 text-center">
                {(currentRole === 'team_leader' || currentRole === 'normal_user')
                  ? 'Select a user to test RLS policies'
                  : 'Switch roles to preview permissions'}
              </p>
            </div>
          </div>
        )}

        {/* Collapsed indicator */}
        {!isExpanded && (
          <div className="px-4 py-2 bg-white/10">
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                {(() => {
                  const Icon = currentRoleData.icon;
                  return (
                    <>
                      <Icon className="w-4 h-4 text-white" />
                      <span className="text-white text-xs font-semibold">
                        {currentRoleData.name}
                      </span>
                    </>
                  );
                })()}
              </div>
              {currentUserId && (
                <div className="flex items-center gap-1 ml-6">
                  <UserCircle className="w-3 h-3 text-white/70" />
                  <span className="text-white/70 text-xs">
                    {users.find(u => u.id === currentUserId)?.name || 'User'}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
